import {t, Trans} from '@lingui/macro';
import {Placement} from '@popperjs/core';
import React, {FC} from 'react';
import styled from 'styled-components';

import {SearchContactsTooltipOptions} from '../../../../../../../../src/components/common/dropdowns/search/searchContactsDropdown';
import {SearchContactsDropdownContainer} from '../../../../../../../../src/components/common/dropdowns/search/searchContactsDropdownContainer';
import {CardModel} from '../../../../../../../../src/models/api/cardModel';
import {Localizer} from '../../../../../../../../src/store/stateless/internationalizationSelectors';
import {useLocalize} from '../../../../../../../../srcCommon/internationalization/internationalizationContext';
import {HandlerOfWithOptional, RendererOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';

/*
 * Props.
 */

interface AddContactButtonProps {
  filterSearchResults: (card: CardModel) => boolean;
  computeIsContactDisabled?: (card: CardModel) => boolean;
  onConfirm: HandlerOfWithOptional<CardModel, PopoverTransferProps>;
  placement?: Placement;
  renderTooltip?: RendererOf<SearchContactsTooltipOptions>;
  variant?: ButtonVariantsEnum;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  padding: 0 3px 0 3px;
`;

const StyledActionIcon = styled(Icon)`
  margin-right: 6px;
`;

/*
 * Component.
 */

export const AddContactButton: FC<AddContactButtonProps> = ({
  onConfirm,
  filterSearchResults,
  computeIsContactDisabled,
  renderTooltip,
  placement = 'top-end',
  variant = ButtonVariantsEnum.PRIMARY,
}) => {
  const localize = useLocalize();

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderAddContactButton(isOpen, variant)}
      renderModal={(popoverProps) => (
        <SearchContactsDropdownContainer
          popoverProps={popoverProps}
          placement={placement}
          searchPlaceholder={renderSearchPlaceholder(localize)}
          filterSearchResults={filterSearchResults}
          onConfirm={async (card) => onConfirm(card, popoverProps)}
          computeIsContactDisabled={computeIsContactDisabled}
          renderTooltip={renderTooltip}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderSearchPlaceholder(localize: Localizer) {
  return localize(t('list-contacts-search-placeholder')`Search for a contact to add`);
}

function renderAddContactButton(isOpen: boolean, variant: ButtonVariantsEnum) {
  return (
    <Button variant={variant} hasHover={isOpen}>
      <StyledButtonContentDiv>
        <StyledActionIcon name="addContactSmall" />
        <Trans id="list-contacts-add-contact">Add contact</Trans>
      </StyledButtonContentDiv>
    </Button>
  );
}
